// src/features/medicalSercices/page/CompanyForm.tsx
import { useTranslation } from 'react-i18next';
import { API_ENDPOINTS, QUERY_KEYS } from '../../api/apiEndpoints';
import DynamicForm, { type DynamicFormSchema } from '@/common/components/DynamicForm/DynamicForm';

export default function CompanyForm() {
    const { t, i18n } = useTranslation();
    const isArabic = i18n.language === 'ar';

    const companyFormSchema: DynamicFormSchema = {
        layout: "vertical",
        showResetButton: true,
        sections: [
            {
                key: "basic-info",
                title: "Basic Information",
                type: "card",
                layout: { type: "grid", gutter: 24 },
                fields: [
                    {
                        name: 'customerId',
                        label: 'Customer',
                        type: 'select',
                        colSpan: 12, // Full width
                        // disableOnEdit: true,
                        required: true,
                        showSearch: true,
                        disableOnEdit: true,
                        requiredMessage: 'Please select a customer.', // Custom required message
                        placeholder: "Select customer",
                        options: [], // Keep this empty as options will be dynamic
                        dynamicOptions: { // Configuration for fetching options from an API
                            url: "/Lookup/GetDropdownData", // Your API endpoint for skills (ensure it returns an array of objects like {id: 1, name: "JavaScript"})
                            method: "POST", // HTTP method for the request
                            body: { "dropdowns": ["customer"] },
                            // JavaScript function string to transform the API response into { label, value } format
                            transformData: "(apiResponse) => apiResponse.customer.map(item => ({ label: item.Name, value: item.Id }))"
                        }
                    },
                    {
                        name: "name",
                        label: "Name",
                        type: "input",
                        required: true,
                        pattern: "AlphanumericWithSpaceAndSymbols",
                        validation: [{ max: 255 }],
                        colSpan: 12,
                    },
                    {
                        name: 'status',
                        label: 'Status',
                        type: 'switch',
                        colSpan: 12, // Full width
                        defaultValue: true,
                    },                   
                ],
                 
            },
            {
                key: "contact-info",
                title: "Contact Information",
                type: "card",
                layout: { type: "grid", gutter: 24 },
                fields: [
                    {
                        name: "phoneNo",
                        label: "Phone No",
                        type: "input",
                        pattern: "PhoneNumber",
                        colSpan: 12,
                    },
                    {
                        name: "mobileNo",
                        label: "Mobile No",
                        type: "input",
                        pattern: "MobileNumber",
                        colSpan: 12,
                    },
                    {
                        name: "email",
                        label: "Email",
                        type: "input",
                        pattern: "Email",
                        validation: [{ max: 256 }],
                        colSpan: 12,
                    },
                    {
                        name: "webSite",
                        label: "Website",
                        type: "input",
                        pattern: "Website",
                        validation: [{ max: 255 }],
                        colSpan: 12,
                    },
                ],
            },
            {
                key: "address-info",
                title: "Address Information",
                type: "card",
                layout: { type: "grid", gutter: 24 },
                fields: [
                    {
                        name: 'countryId',
                        label: t('Country'),
                        type: 'select',
                        colSpan: 12, // Full width
                        placeholder: "Select country",
                        required: true,
                        requiredMessage: t("Country is required."),
                        showSearch: true, // Enable search functionality
                        options: [], // Will be filled dynamically
                        defaultValue: 104, // <-- Set Iraq as default
                        dynamicOptions: {
                            url: "/Lookup/GetDropdownData",
                            method: "POST",
                            body: { "dropdowns": ["country"] },
                            transformData: `(apiResponse) => apiResponse.country.map(item => ({ label: ${isArabic ? 'item.NameAr' : 'item.Name'}, value: item.Id }))`,

                        },
                    },
                    {
                        name: 'cityId',
                        label: 'City',
                        type: 'select',
                        required: true,
                        requiredMessage: 'City is required.',
                        showSearch: true, // Enable search functionality
                        colSpan: 12,
                        options: [],
                        dynamicOptions: {
                            url: "/City/SearchByCountryId",
                            method: 'GET',
                            transformData: `(apiResponse) => apiResponse.map(item => ({ label: ${isArabic ? 'item.nameAr' : 'item.name'}, value: item.id }))`,
                            dependsOn: ['countryId'],
                        },
                        disabledWhen: [
                            {
                                field: 'countryId',
                                operator: 'isFalsy',
                            },
                        ],
                    },
                    {
                        name: "area",
                        label: "Area",
                        type: "input",
                        pattern: "Area",
                        validation: [{ max: 100 }],
                        colSpan: 12,
                    },
                    {
                        name: "streetNo",
                        label: "Street No",
                        type: "input",
                        pattern: "StreetNo",
                        validation: [{ max: 50 }],
                        colSpan: 12,
                    },
                    {
                        name: "address",
                        label: "Address",
                        type: "textarea",
                        pattern: "Address",
                        validation: [{ max: 500 }],
                        colSpan: 12,
                    },
                ],
            },
            {
                key: "geo-info",
                title: "Location Coordinates",
                type: "card",
                layout: { type: "grid", gutter: 24 },
                fields: [
                    {
                        name: "latitude",
                        label: "Latitude",
                        type: "input",
                        validation: [
                            {
                                validator: (_: unknown, value: number) => {

                                    if (value > 90) {
                                        return Promise.reject(new Error('Value cannot be greater than 90.'));
                                    }
                                    if (value < -90) {
                                        return Promise.reject(new Error('Value must be at least -90.'));
                                    }
                                    return Promise.resolve();
                                },
                            },
                        ],
                        pattern: 'DecimalWithNegative',
                        colSpan: 12,
                    },
                    {
                        name: "longitude",
                        label: "Longitude",
                        type: "input",
                        validation: [
                            {
                                validator: (_: unknown, value: number) => {

                                    if (value > 180) {
                                        return Promise.reject(new Error('Value cannot be greater than 180.'));
                                    }
                                    if (value < -180) {
                                        return Promise.reject(new Error('Value must be at least -180.'));
                                    }
                                    return Promise.resolve();
                                },
                            },
                        ],
                        pattern: 'DecimalWithNegative',
                        colSpan: 12,
                    },
                    {
                        name: "type",
                        label: "Type",
                        type: "input",
                        hidden: true, // Hide this field from the UI
                        defaultValue: "HealthInsurance", // Default value for the type field

                    },
                ],
            },
            {
                key: "logo-info",
                type: "card",
                title: "logo",
                layout: { type: "grid", gutter: 24 },
                fields: [
                    {
                        name: "note",
                        label: "Note",
                        type: "textarea",
                        pattern: "Note",
                        validation: [{ max: 500 }],
                        colSpan: 12,
                    },
                    {
                        name: "logoFile",
                        label: "Upload Logo",
                        type: "fileUpload",
                        accept: "image/*",
                        uploadUrl: "/company/upload-company-logo", // change if needed
                        fileKey: "companyLogoFile",
                        responseValueKey: "data.path",
                        maxCount: 1,
                        colSpan: 12,
                        updateFieldName: 'logo',
                    },
                    {
                        name: 'logo',
                        label: 'Company Logo',
                        type: 'input',
                        hidden: true,
                        colSpan: 12,
                    },
                    
                    ]
            }
        ],
    };



    return (
        <DynamicForm
            schema={companyFormSchema}
            submitApiUrl={API_ENDPOINTS.PARTICIPANTS.SAVE_COMPANY}
            getByIdApiUrl={API_ENDPOINTS.PARTICIPANTS.GET_COMPANY_BY_ID}
            updateApiUrl={API_ENDPOINTS.PARTICIPANTS.UPDATE_COMPANY}
            refreshTableDataKey={QUERY_KEYS.PARTICIPANTS.COMPANY}
            refreshSecondKey={["dropdownData", "companieswithoutplan"] }
        />
    );
}
